/**
 * BaseService - Generic service class for standardized API operations
 *
 * Provides common CRUD operations, bulk operations, import/export, and analytics
 * All module-specific services should extend this class
 *
 * @example
 * ```typescript
 * class ProductService extends BaseService<Product> {
 *   constructor() {
 *     super('/api/products', 'products');
 *   }
 *
 *   // Add module-specific methods
 *   async getCategories() {
 *     return this.get('/categories');
 *   }
 * }
 * ```
 */

import apiClient from './apiClient';
import { AxiosRequestConfig, AxiosResponse } from 'axios';
import logger from '../utils/logger';

// ========================================
// Generic Types
// ========================================

export interface BaseEntity {
  id?: number | string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  isActive?: boolean;
}

export interface ListParams {
  page?: number;
  limit?: number;
  search?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  filters?: Record<string, any>;
  [key: string]: any;
}

export interface ListResponse<T> {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export interface BulkOperation {
  operation: 'delete' | 'update' | 'activate' | 'deactivate';
  ids: (string | number)[];
  data?: Record<string, any>;
}

export interface BulkResponse {
  success: boolean;
  processed: number;
  failed: number;
  errors?: Array<{ id: string | number; error: string }>;
  message: string;
}

export interface ImportResult {
  success: boolean;
  imported: number;
  failed: number;
  totalRecords?: number; // Alias for total records processed
  successCount?: number; // Alias for imported
  failureCount?: number; // Alias for failed
  errors?: Array<{ row: number; error: string }>;
  message: string;
}

export interface AnalyticsResponse<T = any> {
  data: T;
  summary?: Record<string, any>;
  charts?: Array<{
    type: string;
    data: any[];
    config?: Record<string, any>;
  }>;
}

export interface ExportOptions {
  format?: 'csv' | 'excel' | 'pdf';
  filters?: Record<string, any>;
  columns?: string[];
  filename?: string;
}

// ========================================
// BaseService Class
// ========================================

export class BaseService<T extends BaseEntity = BaseEntity> {
  protected baseUrl: string;
  protected resourceName: string;
  protected client = apiClient;

  /**
   * Create a new BaseService instance
   *
   * @param baseUrl - Base URL for API endpoints (e.g., '/api/products')
   * @param resourceName - Resource name for logging and error messages (e.g., 'products')
   */
  constructor(baseUrl: string, resourceName: string) {
    this.baseUrl = baseUrl;
    this.resourceName = resourceName;
  }

  // ========================================
  // Core HTTP Methods
  // ========================================

  /**
   * Generic GET request
   */
  protected async get<R = any>(
    endpoint: string,
    config?: AxiosRequestConfig
  ): Promise<any> {
    try {
      return await this.client.get<R>(`${this.baseUrl}${endpoint}`, config);
    } catch (error) {
      this.handleError('GET', endpoint, error);
      throw error;
    }
  }

  /**
   * Generic POST request
   */
  protected async post<R = any, D = any>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<any> {
    try {
      return await this.client.post<R>(`${this.baseUrl}${endpoint}`, data, config);
    } catch (error) {
      this.handleError('POST', endpoint, error);
      throw error;
    }
  }

  /**
   * Generic PUT request
   */
  protected async put<R = any, D = any>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<any> {
    try {
      return await this.client.put<R>(`${this.baseUrl}${endpoint}`, data, config);
    } catch (error) {
      this.handleError('PUT', endpoint, error);
      throw error;
    }
  }

  /**
   * Generic PATCH request
   */
  protected async patch<R = any, D = any>(
    endpoint: string,
    data?: D,
    config?: AxiosRequestConfig
  ): Promise<any> {
    try {
      return await this.client.patch<R>(`${this.baseUrl}${endpoint}`, data, config);
    } catch (error) {
      this.handleError('PATCH', endpoint, error);
      throw error;
    }
  }

  /**
   * Generic DELETE request
   */
  protected async delete<R = any>(
    endpoint: string,
    config?: AxiosRequestConfig
  ): Promise<any> {
    try {
      return await this.client.delete<R>(`${this.baseUrl}${endpoint}`, config);
    } catch (error) {
      this.handleError('DELETE', endpoint, error);
      throw error;
    }
  }

  /**
   * Upload file (POST) - uses dedicated uploadFile method for proper FormData handling
   * Content-Type is automatically set by axios when using FormData
   */
  protected async uploadFile<R = any>(
    endpoint: string,
    formData: FormData,
    onProgress?: (progressEvent: any) => void
  ): Promise<any> {
    try {
      return await this.client.uploadFile<R>(`${this.baseUrl}${endpoint}`, formData, onProgress);
    } catch (error) {
      this.handleError('UPLOAD', endpoint, error);
      throw error;
    }
  }

  /**
   * Upload file (PUT) - uses dedicated uploadFilePut method for proper FormData handling
   * Content-Type is automatically set by axios when using FormData
   */
  protected async uploadFilePut<R = any>(
    endpoint: string,
    formData: FormData,
    onProgress?: (progressEvent: any) => void
  ): Promise<any> {
    try {
      return await this.client.uploadFilePut<R>(`${this.baseUrl}${endpoint}`, formData, onProgress);
    } catch (error) {
      this.handleError('UPLOAD_PUT', endpoint, error);
      throw error;
    }
  }

  // ========================================
  // CRUD Operations
  // ========================================

  /**
   * Get paginated list of records
   *
   * @param params - Query parameters for filtering, pagination, sorting
   * @returns Paginated response with data and metadata
   */
  async getList(params?: ListParams): Promise<AxiosResponse<ListResponse<T>>> {
    return this.get<ListResponse<T>>('', { params });
  }

  /**
   * Get a single record by ID
   *
   * @param id - Record ID
   * @returns Single record
   */
  async getById(id: string | number): Promise<AxiosResponse<T>> {
    return this.get<T>(`/${id}`);
  }

  /**
   * Create a new record
   *
   * @param data - Record data
   * @returns Created record
   */
  async create(data: Partial<T>): Promise<AxiosResponse<T>> {
    return this.post<T, Partial<T>>('', data);
  }

  /**
   * Update an existing record
   *
   * @param id - Record ID
   * @param data - Updated record data
   * @returns Updated record
   */
  async update(id: string | number, data: Partial<T>): Promise<AxiosResponse<T>> {
    return this.put<T, Partial<T>>(`/${id}`, data);
  }

  /**
   * Partially update a record (PATCH)
   *
   * @param id - Record ID
   * @param data - Partial record data
   * @returns Updated record
   */
  async partialUpdate(id: string | number, data: Partial<T>): Promise<AxiosResponse<T>> {
    return this.patch<T, Partial<T>>(`/${id}`, data);
  }

  /**
   * Delete a record (soft or hard delete based on backend)
   *
   * @param id - Record ID
   * @returns Deletion confirmation
   */
  async deleteById(id: string | number): Promise<AxiosResponse<void>> {
    return this.delete<void>(`/${id}`);
  }

  /**
   * Restore a soft-deleted record
   *
   * @param id - Record ID
   * @returns Restored record
   */
  async restore(id: string | number): Promise<AxiosResponse<T>> {
    return this.post<T>(`/${id}/restore`);
  }

  // ========================================
  // Bulk Operations
  // ========================================

  /**
   * Perform bulk operations on multiple records
   *
   * @param operation - Bulk operation details
   * @returns Bulk operation result
   */
  async bulkOperation(operation: BulkOperation): Promise<AxiosResponse<BulkResponse>> {
    return this.post<BulkResponse, BulkOperation>('/bulk', operation);
  }

  /**
   * Bulk delete multiple records
   *
   * @param ids - Array of record IDs
   * @returns Bulk deletion result
   */
  async bulkDelete(ids: (string | number)[]): Promise<AxiosResponse<BulkResponse>> {
    return this.bulkOperation({
      operation: 'delete',
      ids,
    });
  }

  /**
   * Bulk update multiple records
   *
   * @param ids - Array of record IDs
   * @param data - Data to update
   * @returns Bulk update result
   */
  async bulkUpdate(ids: (string | number)[], data: Partial<T>): Promise<AxiosResponse<BulkResponse>> {
    return this.bulkOperation({
      operation: 'update',
      ids,
      data,
    });
  }

  /**
   * Bulk activate multiple records
   *
   * @param ids - Array of record IDs
   * @returns Bulk activation result
   */
  async bulkActivate(ids: (string | number)[]): Promise<AxiosResponse<BulkResponse>> {
    return this.bulkOperation({
      operation: 'activate',
      ids,
    });
  }

  /**
   * Bulk deactivate multiple records
   *
   * @param ids - Array of record IDs
   * @returns Bulk deactivation result
   */
  async bulkDeactivate(ids: (string | number)[]): Promise<AxiosResponse<BulkResponse>> {
    return this.bulkOperation({
      operation: 'deactivate',
      ids,
    });
  }

  // ========================================
  // Import / Export
  // ========================================

  /**
   * Import data from file
   * Uses dedicated uploadFile method for proper FormData handling
   *
   * @param file - File to import (CSV, Excel, etc.)
   * @param options - Import options
   * @returns Import result
   */
  async import(file: File, options?: Record<string, any>): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);

    if (options) {
      Object.entries(options).forEach(([key, value]) => {
        formData.append(key, typeof value === 'object' ? JSON.stringify(value) : String(value));
      });
    }

    // Use uploadFile for proper FormData handling (no manual Content-Type)
    return this.uploadFile<ImportResult>('/import', formData);
  }

  /**
   * Export data to file
   *
   * @param options - Export options (format, filters, columns)
   * @returns File blob
   */
  async export(options?: ExportOptions): Promise<AxiosResponse<Blob>> {
    const params: Record<string, any> = {};

    if (options?.format) params.format = options.format;
    if (options?.filters) params.filters = JSON.stringify(options.filters);
    if (options?.columns) params.columns = options.columns.join(',');
    if (options?.filename) params.filename = options.filename;

    return this.get<Blob>('/export', {
      params,
      responseType: 'blob',
    });
  }

  /**
   * Download export file
   *
   * @param options - Export options
   * @param filename - Optional custom filename
   */
  async downloadExport(options?: ExportOptions, filename?: string): Promise<void> {
    const response = await this.export(options);
    const blob = new Blob([response.data], {
      type: (response.headers['content-type'] as string | undefined) || 'application/octet-stream'
    });

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename || options?.filename || `${this.resourceName}_export_${Date.now()}.${options?.format || 'csv'}`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  /**
   * Get import template file
   *
   * @param format - Template format (csv, excel)
   * @returns Template file blob
   */
  async getImportTemplate(format: 'csv' | 'excel' = 'csv'): Promise<AxiosResponse<Blob>> {
    return this.get<Blob>('/import/template', {
      params: { format },
      responseType: 'blob',
    });
  }

  // ========================================
  // Analytics
  // ========================================

  /**
   * Get analytics/insights data
   *
   * @param params - Analytics parameters (date range, filters)
   * @returns Analytics data
   */
  async getAnalytics<A = any>(params?: Record<string, any>): Promise<AxiosResponse<AnalyticsResponse<A>>> {
    return this.get<AnalyticsResponse<A>>('/analytics', { params });
  }

  /**
   * Get summary/statistics
   *
   * @param params - Summary parameters
   * @returns Summary data
   */
  async getSummary<S = any>(params?: Record<string, any>): Promise<AxiosResponse<S>> {
    return this.get<S>('/summary', { params });
  }

  // ========================================
  // Status Operations
  // ========================================

  /**
   * Activate a record
   *
   * @param id - Record ID
   * @returns Updated record
   */
  async activate(id: string | number): Promise<AxiosResponse<T>> {
    return this.post<T>(`/${id}/activate`);
  }

  /**
   * Deactivate a record
   *
   * @param id - Record ID
   * @returns Updated record
   */
  async deactivate(id: string | number): Promise<AxiosResponse<T>> {
    return this.post<T>(`/${id}/deactivate`);
  }

  /**
   * Toggle record active status
   *
   * @param id - Record ID
   * @returns Updated record
   */
  async toggleStatus(id: string | number): Promise<AxiosResponse<T>> {
    return this.post<T>(`/${id}/toggle-status`);
  }

  // ========================================
  // Search and Filter
  // ========================================

  /**
   * Search records
   *
   * @param query - Search query
   * @param params - Additional parameters
   * @returns Search results
   */
  async search(query: string, params?: Record<string, any>): Promise<AxiosResponse<ListResponse<T>>> {
    return this.get<ListResponse<T>>('/search', {
      params: { query, ...params },
    });
  }

  /**
   * Get filtered records
   *
   * @param filters - Filter criteria
   * @param params - Additional parameters
   * @returns Filtered results
   */
  async filter(filters: Record<string, any>, params?: Record<string, any>): Promise<AxiosResponse<ListResponse<T>>> {
    return this.get<ListResponse<T>>('/filter', {
      params: { filters: JSON.stringify(filters), ...params },
    });
  }

  // ========================================
  // Validation and Checks
  // ========================================

  /**
   * Check if record exists
   *
   * @param id - Record ID
   * @returns True if exists, false otherwise
   */
  async exists(id: string | number): Promise<boolean> {
    try {
      await this.get(`/${id}/exists`);
      return true;
    } catch (error) {
      return false;
    }
  }

  /**
   * Validate data before create/update
   *
   * @param data - Data to validate
   * @returns Validation result
   */
  async validate(data: Partial<T>): Promise<AxiosResponse<{ valid: boolean; errors?: any[] }>> {
    return this.post('/validate', data);
  }

  // ========================================
  // Custom Field Support
  // ========================================

  /**
   * Get custom fields configuration
   *
   * @returns Custom fields configuration
   */
  async getCustomFields(): Promise<AxiosResponse<any[]>> {
    return this.get('/custom-fields');
  }

  /**
   * Update custom fields for a record
   *
   * @param id - Record ID
   * @param customFields - Custom field data
   * @returns Updated record
   */
  async updateCustomFields(id: string | number, customFields: Record<string, any>): Promise<AxiosResponse<T>> {
    return this.patch<T>(`/${id}/custom-fields`, { customFields });
  }

  // ========================================
  // Audit and History
  // ========================================

  /**
   * Get audit logs for a record
   *
   * @param id - Record ID
   * @param params - Query parameters
   * @returns Audit logs
   */
  async getAuditLogs(id: string | number, params?: Record<string, any>): Promise<AxiosResponse<any[]>> {
    return this.get(`/${id}/audit-logs`, { params });
  }

  /**
   * Get change history for a record
   *
   * @param id - Record ID
   * @param params - Query parameters
   * @returns Change history
   */
  async getHistory(id: string | number, params?: Record<string, any>): Promise<AxiosResponse<any[]>> {
    return this.get(`/${id}/history`, { params });
  }

  // ========================================
  // Error Handling
  // ========================================

  /**
   * Handle API errors
   *
   * @param method - HTTP method
   * @param endpoint - API endpoint
   * @param error - Error object
   */
  protected handleError(method: string, endpoint: string, error: unknown): void {
    const axiosError = error as { response?: { data?: { message?: string; errors?: unknown; details?: unknown }; status?: number; statusText?: string }; message?: string };
    const message = axiosError?.response?.data?.message || axiosError?.message || 'Unknown error';
    logger.error(`[${this.resourceName.toUpperCase()}] ${method} ${this.baseUrl}${endpoint} failed:`, message);

    // Log detailed validation errors if available
    if (axiosError?.response?.data) {
      logger.error('Error details:', {
        status: axiosError.response.status,
        statusText: axiosError.response.statusText,
        data: axiosError.response.data,
        errors: axiosError.response.data.errors,
        details: axiosError.response.data.details,
      });
    }

    // Additional error handling can be added here (e.g., toast notifications, logging)
  }

  // ========================================
  // Helper Methods
  // ========================================

  /**
   * Build query params from object
   *
   * @param params - Parameters object
   * @returns URLSearchParams
   */
  protected buildParams(params: Record<string, any>): URLSearchParams {
    const searchParams = new URLSearchParams();

    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        if (typeof value === 'object') {
          searchParams.append(key, JSON.stringify(value));
        } else {
          searchParams.append(key, String(value));
        }
      }
    });

    return searchParams;
  }

  /**
   * Get full URL for an endpoint
   *
   * @param endpoint - Endpoint path
   * @returns Full URL
   */
  protected getUrl(endpoint: string): string {
    return `${this.baseUrl}${endpoint}`;
  }
}

export default BaseService;